import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tKey } from 'ember-intl';
import GeneralDatepickersDatepickerWithSidebar from 'lumen/components/general/datepickers/datepicker-with-sidebar';

interface HotelBenchmarkInsightDatePickersDatePickerSignature {
  Args: {
    rangeChanged: unknown;
    selectedDateRange: unknown;
  };
}

export default class HotelBenchmarkInsightDatePickersDatePicker extends Component<HotelBenchmarkInsightDatePickersDatePickerSignature> {
  @service declare access: Services['access'];
  @service('benchmark-insight/beni-service')
  declare benchmarkInsightBeniService: Services['benchmark-insight/beni-service'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];

  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMaxDate() {
    const { hasBenchmarkInsightFutureData } = this.access;
    const lastAvailableStayDateString =
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.benchmarkInsightLastAvailableStayDate;

    if (!hasBenchmarkInsightFutureData) {
      const { lastPastDate } = this.benchmarkInsightBeniService;

      return lastPastDate;
    }
    // @ts-expect-error: Incorrect type
    return this.hotelAndMarketService.toLocalTime(lastAvailableStayDateString);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMinDate() {
    const firstAvailableStayDate =
      // @ts-expect-error: Incorrect type
      this.hotelAndMarketService.hotel.benchmarkInsightFirstAvailableStayDate;
    // @ts-expect-error: Incorrect type
    return this.hotelAndMarketService.toLocalTime(firstAvailableStayDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerRangeOptions() {
    const { hasBenchmarkInsightFutureData } = this.access;
    const { lastPastDate } = this.benchmarkInsightBeniService;

    const todayMoment = this.hotelAndMarketService.getLocalTime();

    // Options for when future data is allowed
    const nextThirtyDaysOption = {
      text: 'general.next_days',
      count: 30,
      id: 'thirty_days',
      isSelected: true,
      range: {
        start: todayMoment,
        end: todayMoment.add(30, 'day'),
      },
    };

    const nextSixtyDaysOption = {
      text: 'general.next_days',
      count: 60,
      id: 'sixty_days',
      isSelected: false,
      range: {
        start: todayMoment,
        end: todayMoment.add(60, 'day'),
      },
    };

    const nextNinetyDaysOption = {
      text: 'general.next_days',
      count: 90,
      id: 'ninety_days',
      isSelected: false,
      range: {
        start: todayMoment,
        end: todayMoment.add(90, 'day'),
      },
    };

    const currentMonthOption = {
      text: 'revenue_insight.current_month',
      id: 'current_month',
      isSelected: false,
      range: {
        start: todayMoment.startOf('month'),
        end: todayMoment.endOf('month'),
      },
    };

    const currentQuarterOption = {
      text: 'revenue_insight.current_quarter',
      id: 'quarter',
      isSelected: false,
      range: {
        start: todayMoment.startOf('quarter'),
        end: todayMoment.endOf('quarter'),
      },
    };

    const fullYearOption = {
      text: 'revenue_insight.full_year',
      id: 'full_year',
      isSelected: false,
      range: {
        start: todayMoment.startOf('year'),
        end: todayMoment.endOf('year'),
      },
    };

    // Options for when future data is not allowed
    const pastThirtyDaysOption = {
      text: tKey('benchmark_insight.past_days'),
      count: 30,
      id: 'past_thirty_days',
      isSelected: true,
      range: {
        start: lastPastDate.subtract(30, 'day'),
        end: lastPastDate,
      },
    };

    const pastSixtyDaysOption = {
      text: tKey('benchmark_insight.past_days'),
      count: 60,
      id: 'past_sixty_days',
      isSelected: false,
      range: {
        start: lastPastDate.subtract(60, 'day'),
        end: lastPastDate,
      },
    };

    const pastNinetyDaysOption = {
      text: tKey('benchmark_insight.past_days'),
      count: 90,
      id: 'past_ninety_days',
      isSelected: false,
      range: {
        start: lastPastDate.subtract(90, 'day'),
        end: lastPastDate,
      },
    };

    const monthToDateOption = {
      text: tKey('revenue_insight.month_to_date'),
      id: 'month_to_date',
      isSelected: false,
      range: {
        start: lastPastDate.startOf('month'),
        end: lastPastDate,
      },
    };

    const quarterToDateOption = {
      text: tKey('benchmark_insight.datepicker.quarter_to_date'),
      id: 'quarter_to_date',
      isSelected: false,
      range: {
        start: lastPastDate.startOf('quarter'),
        end: lastPastDate,
      },
    };

    const yearToDateOption = {
      text: 'revenue_insight.year_to_date',
      id: 'year_to_date',
      isSelected: false,
      range: {
        start: lastPastDate.startOf('year'),
        end: lastPastDate,
      },
    };

    // Always available
    const customOption = {
      text: 'general.custom',
      id: 'custom',
      isSelected: false,
      isDisabled: false,
      range: {
        start: null,
        end: null,
      },
    };

    const optionsWithFutureData = [
      nextThirtyDaysOption,
      nextSixtyDaysOption,
      nextNinetyDaysOption,
      currentMonthOption,
      currentQuarterOption,
      yearToDateOption,
      fullYearOption,
      customOption,
    ];

    const optionsWithoutFutureData = [
      pastThirtyDaysOption,
      pastSixtyDaysOption,
      pastNinetyDaysOption,
      monthToDateOption,
      quarterToDateOption,
      yearToDateOption,
      customOption,
    ];

    const options = hasBenchmarkInsightFutureData
      ? optionsWithFutureData
      : optionsWithoutFutureData;

    return [
      {
        id: 'references',
        title: this.intl.t('revenue_insight.report_selected_period'),
        options,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dateTooltips() {
    // If the user doesn't have access to future data, show a tooltip when hovering over days in the future in the calendar of the date picker.
    if (!this.access.hasBenchmarkInsightFutureData) {
      const startDate = this.benchmarkInsightBeniService.lastPastDate;

      return [
        {
          startDate,
          tooltipText: this.intl.t(
            'benchmark_insight.date_picker.day_tooltip.future_dates',
          ),
        },
      ];
    }

    return [];
  }

  <template>
    {{! @glint-expect-error: Incorrect type }}
    <GeneralDatepickersDatepickerWithSidebar
      @dateTooltips={{this.dateTooltips}}
      @displayEvents={{false}}
      @maxDate={{this.datePickerMaxDate}}
      @maxRange="1 year"
      @minDate={{this.datePickerMinDate}}
      @range={{@selectedDateRange}}
      @rangeChanged={{@rangeChanged}}
      @selectSingleDay={{false}}
      @sidebarLists={{this.datePickerRangeOptions}}
    />
  </template>
}
